import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SseService } from './sse.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { enviroment } from 'src/environments/environment';
import { TelemetryData, TelemetryHistory, TelemetryConnectionStatus } from '../model/telemetry-data';

/**
 * Interface para coordenadas GPS históricas
 */
export interface CoordinatePoint {
  latitude: number;
  longitude: number;
  timestamp: string;
}

/**
 * Interface para resposta do endpoint de coordenadas históricas
 */
export interface VcuCoordinatesHistoryResponse {
  vcuId: string;
  startTime: string;
  endTime: string;
  recordsCount: number;
  coordinatesCount: number;
  data: CoordinatePoint[];
}

/**
 * Interface para parâmetros de stream realtime
 */
export interface StreamRealtimeParameter {
  analysesName: string;
  name: {
    language: string;
    name: string;
    description: string;
  };
  type: string;
  presets: StreamPreset[];
}

/**
 * Interface para presets de stream
 */
export interface StreamPreset {
  name: string;
  value: {
    channels: (string | null)[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  private currentTelemetry$ = new BehaviorSubject<TelemetryData | null>(null);
  private connectionStatus$ = new BehaviorSubject<TelemetryConnectionStatus>({
    status: 'disconnected'
  });
  private activeCarId: string | null = null;

  constructor(
    private sseService: SseService,
    private http: HttpClient
  ) {}

  /**
   * Conecta ao stream de telemetria em tempo real via SSE
   * @param carId ID do carro/veículo
   * @param eventName Nome do evento SSE (padrão: 'telemetry')
   * @returns Observable com os dados de telemetria
   */
  connectToRealtime(carId: string, eventName: string = 'telemetry'): Observable<TelemetryData> {
    this.activeCarId = carId;
    const url = `${enviroment.BASE_URL_API}/telemetry/stream/${carId}`;
    
    
    this.updateConnectionStatus('reconnecting', 0);
    
    const stream = this.sseService.connect<TelemetryData>(url, eventName);
    
    stream.subscribe({
      next: (data) => {
        this.updateConnectionStatus('connected');
        this.currentTelemetry$.next(data);
      },
      error: (error) => {
        this.updateConnectionStatus('disconnected');
      }
    });

    return stream;
  }

  /**
   * Desconecta do stream de telemetria
   */
  disconnect(): void {
    this.sseService.disconnect();
    this.updateConnectionStatus('disconnected');
    this.currentTelemetry$.next(null);
    this.activeCarId = null;
  }

  /**
   * Obtém o último dado de telemetria recebido (cache local)
   */
  getCurrentTelemetry(): Observable<TelemetryData | null> {
    return this.currentTelemetry$.asObservable();
  }

  /**
   * Status da conexão SSE
   */
  getConnectionStatus(): Observable<TelemetryConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Busca última posição conhecida do Redis via API
   * @param carId ID do carro
   */
  getLastKnownPosition(carId: string): Observable<TelemetryData> {
    return this.http.get<TelemetryData>(
      `${enviroment.BASE_URL_API}/telemetry/last/${carId}`
    ).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Busca histórico de telemetria do AWS Timestream via API Gateway
   * @param carId ID do carro
   * @param startTime Data/hora inicial
   * @param endTime Data/hora final
   * @param limit Limite de pontos (opcional)
   */
  getHistory(
    carId: string, 
    startTime: Date, 
    endTime: Date,
    limit?: number
  ): Observable<TelemetryHistory> {
    let params = new HttpParams()
      .set('carId', carId)
      .set('startTime', startTime.toISOString())
      .set('endTime', endTime.toISOString());
    
    if (limit) {
      params = params.set('limit', limit.toString());
    }
    
    return this.http.get<TelemetryHistory>(
      `${enviroment.BASE_URL_API}/telemetry/history`,
      { params }
    ).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Busca histórico das últimas N horas
   * @param carId ID do carro
   * @param hours Quantidade de horas
   */
  getRecentHistory(carId: string, hours: number = 24): Observable<TelemetryHistory> {
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - (hours * 60 * 60 * 1000));
    
    return this.getHistory(carId, startTime, endTime);
  }

  /**
   * Verifica se um carro está online
   * @param carId ID do carro
   */
  isCarOnline(carId: string): Observable<boolean> {
    return this.http.get<{ online: boolean }>(
      `${enviroment.BASE_URL_API}/telemetry/status/${carId}`
    ).pipe(
      map(response => response.online),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista todos os carros com telemetria ativa
   */
  getActiveCars(): Observable<string[]> {
    return this.http.get<string[]>(
      `${enviroment.BASE_URL_API}/telemetry/active`
    ).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Conecta ao stream SSE da VCU
   * @param vcuId ID da VCU
   * @param eventName Nome do evento SSE (padrão: 'vcu-telemetry')
   * @returns Observable com os dados da VCU
   */
  connectToVcuStream(vcuId: string, eventName: string = 'vcu-telemetry'): Observable<any> {
    this.activeCarId = vcuId;
    var accessToken = sessionStorage.getItem('idToken') || '';
    const url = `${enviroment.BASE_URL_SSE}/streaming/sse/stream/vcu/${vcuId}?access_token=${accessToken}`;
    
    
    this.updateConnectionStatus('reconnecting', 0);
    
    // Conecta ao SSE sem autenticação (useAuth: false)
    const stream = this.sseService.connect<any>(url, eventName, false);
    
    stream.subscribe({
      next: (data) => {
        this.updateConnectionStatus('connected');
        this.currentTelemetry$.next(data);
      },
      error: (error) => {
        this.updateConnectionStatus('disconnected');
      }
    });

    return stream;
  }

  /**
   * Busca histórico de coordenadas GPS da VCU
   * @param vcuId ID da VCU
   * @param startTime Data/hora inicial no formato ISO (ex: '2026-01-29T14:11:59')
   * @param endTime Data/hora final no formato ISO (ex: '2026-01-30T13:11:59')
   * @returns Observable com o histórico de coordenadas
   */
  getVcuCoordinatesHistory(
    vcuId: string,
    startTime: string,
    endTime: string
  ): Observable<VcuCoordinatesHistoryResponse> {
    const url = `${enviroment.BASE_URL_SSE}/streaming/sse/telemetry/coordinates`;
    
    const params = new HttpParams()
      .set('vcuId', vcuId)
      .set('startTime', startTime)
      .set('endTime', endTime);

    //console.log('[TelemetryService] Buscando histórico de coordenadas:', { vcuId, startTime, endTime });

    return this.http.get<VcuCoordinatesHistoryResponse>(url, { params }).pipe(
      map(response => {
        //console.log(`[TelemetryService] Histórico recebido: ${response.coordinatesCount} coordenadas`);
        return response;
      }),
      catchError(error => {
        //console.error('[TelemetryService] Erro ao buscar histórico de coordenadas:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém ID do carro atualmente conectado
   */
  getActiveCarId(): string | null {
    return this.activeCarId;
  }

  /**
   * Busca os parâmetros de stream realtime do usuário
   * @param userId ID do usuário
   * @param language Idioma (ex: 'pt-br')
   * @returns Observable com os parâmetros de stream
   */
  getStreamRealtimeParameters(userId: string, language: string): Observable<StreamRealtimeParameter[]> {
    const url = `${enviroment.BASE_URL_API}/global/parameter_tasks`;
    
    const params = new HttpParams()
      .set('userID', userId)
      .set('language', language);

    return this.http.get<StreamRealtimeParameter[]>(url, { params }).pipe(
      map(response => {
        //console.log('[TelemetryService] Parâmetros de stream recebidos:', response);
        return response;
      }),
      catchError(error => {
        //console.error('[TelemetryService] Erro ao buscar parâmetros de stream:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Atualiza o status da conexão
   */
  private updateConnectionStatus(
    status: 'connected' | 'disconnected' | 'reconnecting',
    reconnectAttempts?: number
  ): void {
    this.connectionStatus$.next({
      status,
      lastUpdate: new Date(),
      reconnectAttempts
    });
  }
}
